import type { AppRole } from "./roles";
import { ROLE_GROUPS, ROLES } from "./roles";

const ADMIN = ROLE_GROUPS.ADMIN;
const MANAGEMENT = ROLE_GROUPS.MANAGEMENT;
const POS = ROLE_GROUPS.POS;
const KITCHEN = ROLE_GROUPS.KITCHEN;
const ALL = ROLE_GROUPS.ALL;
const OPERATIONS: readonly AppRole[] = [
  ROLES.OWNER,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.CASHIER,
  ROLES.KITCHEN,
];

/** Default landing path after login, switcher change, or redirect from a disallowed route. */
export function getDefaultLandingForRole(role: AppRole): string {
  if (role === ROLES.OWNER || role === ROLES.ADMIN || role === ROLES.MANAGER) {
    return "/admin";
  }

  if (role === ROLES.CASHIER) {
    return "/admin/terminal";
  }
  if (role === ROLES.KITCHEN) {
    return "/admin/kitchen";
  }
  if (role === ROLES.STAFF) {
    return "/admin/staff/timeclock";
  }
  return "/admin";
}

export const ROUTE_PERMISSIONS: Record<string, readonly AppRole[]> = {
  "/admin": ALL,
  "/admin/menu": MANAGEMENT,
  "/admin/order-types": MANAGEMENT,
  "/admin/integrations": ADMIN,
  "/admin/analytics": MANAGEMENT,
  "/admin/customers": MANAGEMENT,
  "/admin/invoices": MANAGEMENT,
  "/admin/staff": MANAGEMENT,
  "/admin/notification-templates": MANAGEMENT,
  "/admin/settings": ADMIN,
  "/admin/terminal-settings": ADMIN,
  "/admin/reports": MANAGEMENT,
  "/admin/reports/z-report": MANAGEMENT,
  "/admin/reports/period": MANAGEMENT,
  "/admin/lunch-schedule": MANAGEMENT,
  "/admin/timestamp-menu": MANAGEMENT,
  "/admin/menu-style": MANAGEMENT,
  "/admin/menu-designer": MANAGEMENT,
  "/admin/bookings-overview": MANAGEMENT,
  "/admin/reservations": MANAGEMENT,
  "/admin/reservation-settings": MANAGEMENT,
  "/admin/email-campaigns": MANAGEMENT,
  "/admin/marketing": MANAGEMENT,
  "/admin/social-media": MANAGEMENT,
  "/admin/loyalty/members": MANAGEMENT,
  "/admin/loyalty/settings": MANAGEMENT,
  "/admin/loyalty/rewards": MANAGEMENT,
  "/admin/loyalty/vouchers": MANAGEMENT,
  "/admin/loyalty/topup": MANAGEMENT,
  "/admin/loyalty/push": MANAGEMENT,
  "/admin/loyalty/redemptions": MANAGEMENT,
  "/admin/framer-sync": ADMIN,
  "/admin/staff/schedule": MANAGEMENT,
  "/admin/staff/overview": MANAGEMENT,
  "/admin/staff/leave": MANAGEMENT,
  "/admin/signage/devices": ADMIN,
  "/admin/signage/groups": ADMIN,
  "/admin/signage/assets": ADMIN,
  "/admin/signage/playlists": ADMIN,
  "/admin/signage/schedules": ADMIN,
  "/admin/signage/zones": ADMIN,
  "/admin/signage/alerts": ADMIN,
  "/admin/signage/analytics": ADMIN,
  "/admin/services": ADMIN,
  "/admin/services/resources": ADMIN,
  "/admin/services/calendar": ADMIN,
  "/admin/services/bookings": ADMIN,
  "/admin/services/settings": ADMIN,
  "/admin/ai/dashboard": ADMIN,
  "/admin/ai/price-intelligence": ADMIN,
  "/admin/ai/trends": ADMIN,
  "/admin/ai/menu-optimizer": ADMIN,
  "/admin/ai/recipes": ADMIN,
  "/admin/ai/waste": ADMIN,
  "/admin/ai/staff-optimizer": ADMIN,
  "/admin/ai/customer-insights": ADMIN,
  "/admin/ai/history": ADMIN,
  "/admin/inventory": MANAGEMENT,
  "/admin/article-import": MANAGEMENT,
  "/admin/projects": MANAGEMENT,
  "/admin/todo": MANAGEMENT,
  "/admin/on-demand": MANAGEMENT,
  "/admin/on-demand/credits": MANAGEMENT,
  "/admin/on-demand/catalog": MANAGEMENT,
  "/admin/on-demand/new-request": MANAGEMENT,
  "/admin/on-demand/requests": MANAGEMENT,
  "/admin/on-demand/projects": MANAGEMENT,
  "/admin/on-demand/deliverables": MANAGEMENT,
  "/admin/on-demand/settings": MANAGEMENT,
  "/admin/on-demand/ops": MANAGEMENT,
  "/admin/on-demand/calendar": MANAGEMENT,
  "/admin/on-demand/brands": MANAGEMENT,
  "/admin/inbox": MANAGEMENT,
  "/admin/inbox/standups": MANAGEMENT,
  /** Legacy alias — same screen as standups (saved links / external dashboards). */
  "/admin/inbox/team-reports": MANAGEMENT,
  "/admin/inbox/analytics": MANAGEMENT,
  "/admin/inbox/decisions": MANAGEMENT,
  "/admin/quotes": MANAGEMENT,
  "/admin/suppliers": MANAGEMENT,
  "/admin/events": MANAGEMENT,
  "/admin/reviews": MANAGEMENT,
  "/admin/gdpr": ADMIN,
  "/admin/locations": ADMIN,
  "/admin/roles": ADMIN,
  "/admin/delivery-tracking": MANAGEMENT,
  "/admin/happy-hour": MANAGEMENT,
  "/admin/offline": ADMIN,
  /** Tag library used from menu / marketing — same cohort as menu management. */
  "/admin/tags": MANAGEMENT,
  "/admin/net": ADMIN,

  "/admin/orders": OPERATIONS,
  "/admin/express-checkout": POS,
  "/admin/customer-display": OPERATIONS,
  "/admin/tables": POS,
  "/admin/profile": ALL,
  "/admin/staff/timeclock": ALL,
  "/admin/kitchen": KITCHEN,
  "/admin/terminal": POS,
};

/** Paths with dynamic segments (e.g. UUID) — longest prefix wins after exact ROUTE_PERMISSIONS miss. */
const ADMIN_PATH_PREFIX_PERMISSIONS: {
  prefix: string;
  roles: readonly AppRole[];
}[] = [
  { prefix: "/admin/on-demand/requests/", roles: MANAGEMENT },
  { prefix: "/admin/quotes/", roles: MANAGEMENT },
];

export function normalizeAdminPath(path: string): string {
  if (!path || path === "/") return path;
  const pathnameOnly = path.split("?")[0]?.split("#")[0] ?? path;
  const trimmed = pathnameOnly.endsWith("/")
    ? pathnameOnly.slice(0, -1)
    : pathnameOnly;
  return trimmed || "/";
}

/** Unknown /admin paths: no roles (secure-by-default for sidebar and future guards). */
export function allowedRolesForPath(path: string): readonly AppRole[] {
  const normalized = normalizeAdminPath(path);
  const exact = ROUTE_PERMISSIONS[normalized];
  if (exact && exact.length > 0) return exact;
  for (const { prefix, roles } of ADMIN_PATH_PREFIX_PERMISSIONS) {
    if (normalized.startsWith(prefix)) return roles;
  }
  return [];
}

export function canAccessPath(
  path: string,
  role: AppRole | null | undefined,
  isSuperAdmin = false
): boolean {
  if (isSuperAdmin) return true;
  if (!role) return false;
  const allowed = allowedRolesForPath(path);
  if (allowed.length === 0) return false;
  return allowed.includes(role);
}

/** @alias canAccessPath — plan naming */
export const isPathAllowedForRole = canAccessPath;
